/**
 * Analytics report — the operator's on-demand traffic report: the four knobs
 * the operator turns (range, visit gap, channel, campaign), the answer the
 * reconciler last brought back, and the one view model the panel renders. Pure
 * state, zero I/O: `web/src/analytics/report/report.sync.ts` calls the
 * callable and is the sole writer of the answer.
 *
 * ── TWO CAMPAIGN AXES, AND WHY THERE HAD TO BE TWO ─────────────────────────
 * `utmSources` scopes by `utm_source` and `utmCampaigns` by `utm_campaign`,
 * and neither substitutes for the other: the paid creative substitutes
 * `{{site_source_name}}` into `utm_source`, which resolves to one of five
 * two-letter platform codes every paid campaign this deployment runs shares,
 * so a source can only ever name WHICH SURFACE a visit came off — never which
 * run paid for it.
 *
 * Each is empty-means-all, and the two are read together as a CONJUNCTION:
 * a visit counts when its first touch names one of the picked sources AND one
 * of the picked campaigns, which is what makes "the Instagram half of this one
 * run" askable. The callable applies exactly this — one skip per axis over the
 * same buyer — so the panel never has to reconcile a second reading of it.
 *
 * The subject resolves itself. `consoleEventId` names the event whose console
 * is mounted and clears when it disconnects, so the panel takes no property
 * from the dashboard it stands in and reports that event alone.
 *
 * Revenue is the one figure the answer does not carry: `AnalyticsDailySchema`
 * holds no money, so the orders themselves are read for the range and summed
 * per day beside it — money is read live, from the records that own it, so a
 * refund landing after a day closed is never contradicted by a frozen copy.
 *
 * Sibling to `analytics.state.ts`, which is the storefront emitter — a
 * different subject entirely: one records what a visitor did, this one asks
 * what all of them added up to.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { MessageKey, TextKey } from "@momo/i18n";
import type { AnalyticsReport, Buyer } from "@momo/types";
import { consoleEventId } from "../events/console/console.state.ts";
import type { FigureUnit } from "./analytics-figure.view.ts";

// Figures are grouped in one locale across the whole admin surface, the
// console's own stat cells included — an operator comparing two screens is
// comparing numbers, not reading prose. It pins PUNCTUATION and nothing else:
// a grouping separator and a decimal mark are conventions about a number,
// where a weekday, a month name and a twelve-hour clock are words in a
// language and follow the reader instead.
export const FIGURE_LOCALE = "en";

const DAY_MS = 86_400_000;

/** The gap the contract itself defaults to, and the only value whose answer
 *  is ever cached. */
export const DEFAULT_VISIT_GAP_MINUTES = 30;

/** The slider's ladder, which is the clamp the callable contract already
 *  applies — an operator cannot ask for a gap the answer would silently
 *  narrow. */

/** How far back the raw timeline still reaches — the picker's own floor, so
 *  an operator cannot ask for a range the data cannot answer. It clamps every
 *  path into that one picker, its presets included, not the calendar alone. */
export const RAW_WINDOW_DAYS = 90;

export type ReportRangePreset = "today" | "7d" | "30d";

export type ReportRange =
	| { readonly kind: "preset"; readonly preset: ReportRangePreset }
	| { readonly kind: "custom"; readonly from: string; readonly to: string };

export const RANGE_PRESET_DAYS: Readonly<Record<ReportRangePreset, number>> = {
	today: 1,
	"7d": 7,
	"30d": 30,
};

export const CLIENT_DIMENSIONS = [
	"os",
	"browser",
	"device",
	"viewport",
	"orientation",
] as const satisfies ReadonlyArray<
	keyof AnalyticsReport.ClientFilter & keyof Buyer.ClientCounts
>;

export type ClientDimension = (typeof CLIENT_DIMENSIONS)[number];
export type ClientFilterValue = NonNullable<
	AnalyticsReport.ClientFilter[ClientDimension]
>;

export type ActiveClientFilter = {
	readonly dimension: ClientDimension;
	readonly value: ClientFilterValue;
};

export type ReportKnobs = {
	readonly range: ReportRange;
	readonly visitGapMinutes: number;
	readonly utmSources: readonly string[];
	/** The `utm_campaign` values every figure is scoped to — the paid runs'
	 *  own Meta campaign ids, which `CampaignCostRow.metaCampaignId` carries
	 *  and the cost panel picks from. Empty reports every campaign. */
	readonly utmCampaigns: readonly string[];
	/** The closed client cohort. Optional only for old in-memory witnesses; the
	 *  state always seeds it and the request adapters send an empty object when
	 *  no dimension is selected. */
	readonly client?: AnalyticsReport.ClientFilter;
};

/** The one request both app reconcilers observe. Refresh is deliberately not
 * part of this value: the callable input is strict, so the refresh signal
 * rides beside it in each reconciler's distinct/debounce key. */
export type ReportRequest = Omit<
	AnalyticsReport.AnalyticsReportInputPayload,
	"orgId"
>;

export function reportRequestFromKnobs(
	knobs: ReportKnobs,
	eventId: string | null,
	now: number,
): ReportRequest {
	return {
		range: resolveRange(knobs.range, now),
		visitGapMinutes: knobs.visitGapMinutes,
		eventId,
		utmSources: [...knobs.utmSources],
		utmCampaigns: [...knobs.utmCampaigns],
		client: knobs.client ?? {},
	};
}

export const reportKnobs = state<ReportKnobs>("admin/analytics-knobs").memory({
	range: { kind: "preset", preset: "7d" },
	visitGapMinutes: DEFAULT_VISIT_GAP_MINUTES,
	utmSources: [],
	utmCampaigns: [],
	client: {},
});

export type ReportState = {
	/** The last answer, held across a recompute so a fresh call never blanks
	 *  what the operator is reading. Null until one has ever arrived. Revenue
	 *  rides on it beside the rows — never cached, because a refund changes a
	 *  closed day's money — so this panel reads no orders of its own. */
	readonly answer: AnalyticsReport.AnalyticsReportOutput | null;
	readonly computing: boolean;
	readonly failed: boolean;
};

export const reportState = state<ReportState>("admin/analytics-report").memory({
	answer: null,
	computing: false,
	failed: false,
});

/** Bumped on a manual refresh press. The reconciler folds it into the request
 *  it compares for change, so pressing refresh reruns the same range even
 *  though nothing about the range itself moved. */
export const reportRefreshNonce = state<number>(
	"admin/analytics-refresh-nonce",
).memory(0);

export function requestReportRefresh(): void {
	reportRefreshNonce.set(reportRefreshNonce.value + 1);
}

/** One calendar day in the operator's own zone — the unit the report range
 *  is expressed in, and the same unit the answer's rows are keyed by. */
export function calendarDay(at: Date): string {
	return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(at.getDate()).padStart(2, "0")}`;
}

/** The range a knob setting names, resolved against a clock. Read by the
 *  reconciler to build its call and by the picker to clamp its own floor. */
export function resolveRange(
	range: ReportRange,
	now: number,
): { readonly from: string; readonly to: string } {
	if (range.kind === "custom") return { from: range.from, to: range.to };
	const days = RANGE_PRESET_DAYS[range.preset];
	return {
		from: calendarDay(new Date(now - (days - 1) * DAY_MS)),
		to: calendarDay(new Date(now)),
	};
}

/** The event every figure on this panel is scoped to — the console whose
 *  dashboard mounts it. What has no meaning under an event scope, the
 *  landing→event step, is withheld on it. */
export const reportScopeEventId = computed<string | null>(
	() => consoleEventId.value,
);

/** One point of a trend: what it counted, and the calendar day it counted on.
 *  The day crosses UNPRINTED — a month name is a word in the reader's language
 *  and a computed has no host to ask who is reading, so the view prints it
 *  against its own locale on the way into the plot. */
export type TrendPoint = { readonly value: number; readonly date: string };

/** Every tile's own copy key, as literals — `translate` admits an exact key
 *  and a widened `MessageKey` also covers the list keys it cannot render. */
const KPI_LABEL = {
	revenue: "admin.analytics.kpiRevenue",
	visits: "admin.analytics.kpiVisits",
	visitors: "admin.analytics.kpiVisitors",
	conversion: "admin.analytics.kpiConversion",
	returning: "admin.analytics.kpiReturning",
	dwell: "admin.analytics.kpiDwell",
} as const satisfies Readonly<Record<string, MessageKey>>;

/** What rides under a tile's figure. A figure with a history over the range
 *  carries its own course; a rate carries its fill against its own ceiling;
 *  a figure with neither carries nothing rather than a flat line that would
 *  read as one. */
export type KpiVisual =
	| { readonly kind: "series"; readonly points: readonly number[] }
	| { readonly kind: "meter"; readonly percent: number };

export type KpiTile = {
	readonly key: keyof typeof KPI_LABEL;
	readonly labelKey: (typeof KPI_LABEL)[keyof typeof KPI_LABEL];
	/** The raw figure, for the element that prints it. */
	readonly value: number;
	/** What the figure counts, which is what decides how the figure element
	 *  prints it. */
	readonly unit: FigureUnit;
	/** Printed instead of the figure's own formatting, and only where this
	 *  surface owns the form: an em dash for a rate nothing was measured
	 *  against. Empty otherwise. */
	readonly display: string;
	/** The signed change across the range: its later half against its earlier
	 *  half, in percent. Null where the range is too short to have two halves
	 *  or the earlier half is zero — a delta is never invented. */
	readonly deltaPercent: number | null;
	readonly visual: KpiVisual | null;
	/** One line under the figure, in the row the visual would have taken —
	 *  why the tile has no rate to state. Gated on the coverage word and never
	 *  on the missing rate itself, which is also missing on a complete answer
	 *  where nobody came: that dash means what it says and carries no line.
	 *  Null on every tile that has its answer. */
	readonly noteKey: TextKey | null;
};

export type FunnelRow = {
	readonly key: string;
	readonly labelKey: (typeof FUNNEL_STAGES)[number]["labelKey"];
	readonly value: number;
	readonly share: number;
	/** How many did not reach the next stage — null on the last row, which
	 *  has no next stage to fall short of. */
	readonly droppedAfter: number | null;
	/** Printed instead of the figure, on the one condition this surface owns
	 *  the form: an em dash where NO day of the range could be established, so
	 *  a band nothing was recorded for never reads as a counted zero. Empty
	 *  otherwise — a band counted over the days that can be answered prints its
	 *  own count, marked approximate where those days were read short — exactly
	 *  as a KPI tile's own display. */
	readonly display: string;
};

/**
 * What the bands, their drop-off breakdown and the funnel's own shares were
 * counted over. A day whose `checkout_stage` arm cannot be read is excluded
 * from all three rather than blanking them: nothing reconciles such a day
 * later — the carts it named are gone — so a range straddling the retention
 * floor is the ordinary case, and withholding the whole period for it answers
 * the operator's question with nothing when most of it can be answered.
 *
 * Distinct from `coverage.funnel`, which stays the period's honest
 * worst-of-every-day verdict and is what the two cross-arm rates rest on: a
 * numerator counted over three days against a denominator counted over seven
 * is wrong in a direction nothing on this panel can name.
 */
export type FunnelCoverage = {
	/** How much of the range could be counted at all. `none` is the one case
	 *  the panel withholds every band; on `some` it counts the days it can and
	 *  names the gap in words. */
	readonly span: "whole" | "some" | "none";
	/** The two figures the gap note prints. */
	readonly countedDays: number;
	readonly rangeDays: number;
	/** The worst verdict among the COUNTED days alone, so a day the bands
	 *  exclude never marks the bands it is not part of. `unavailable` exactly
	 *  where `span` is `none`. */
	readonly reading: AnalyticsReport.Coverage;
};

export type OutcomeEntry = {
	readonly key: string;
	readonly labelKey: (typeof OUTCOMES)[number]["labelKey"];
	readonly count: number;
	/** A refusal is a status the system reports; a buyer who walked away did
	 *  nothing wrong, and neutral is what says so. */
	readonly tone: "error" | "neutral";
};

/** The row every visit that named no campaign lands in. A sentinel rather
 *  than a source, because "no campaign" is not one — the view reads its own
 *  label for it, and no real `utm_source` can collide with the empty string
 *  the contract's own `.min(1)` forbids. */
export const DIRECT_CHANNEL = "";

export type CampaignRow = {
	readonly source: string;
	readonly visits: number;
	/** Null on the direct row alone: a visit that named no campaign cannot be
	 *  traced to the basket it later started, so its bar carries no paid fill
	 *  and its rate reads as unanswered rather than as a zero somebody
	 *  measured. */
	readonly paid: number | null;
};

export type RetrievalRow = {
	readonly key: Buyer.OrderAction;
	readonly labelKey: (typeof RETRIEVAL_CHANNELS)[number]["labelKey"];
	readonly value: number;
};

/**
 * One paid campaign as this panel reports on it: the contract's own row, plus
 * the two things it deliberately does not carry.
 *
 * The row is carried whole rather than flattened — `attributed` (Meta's claim
 * about its own delivery) and `observed` (what this shop watched happen) stay
 * the two separate halves the contract declares, because showing them side by
 * side and saying which is which is the panel's whole job here.
 */
export type CampaignCostRow = AnalyticsReport.CampaignCost & {
	/** `spendMinor` over tickets sold, in the AD ACCOUNT's own minor unit —
	 *  never measured against `observed.revenueCents`, which counts euro cents
	 *  the spend may not be counted in at all. Null while nothing has sold: the
	 *  denominator is legitimately zero for most of a campaign's life, which is
	 *  why the contract refuses the ratio, and the panel says so in words rather
	 *  than printing an infinity. */
	readonly costPerTicketMinor: number | null;
	readonly trafficLabelKey: TextKey;
	/** The sentence without which three of the four verdicts render as one
	 *  identical zero. */
	readonly trafficWhyKey: TextKey;
	/** `untraceable` is the only verdict that reports a fault, and the only one
	 *  toned as one. */
	readonly trafficTone: "neutral" | "success" | "error";
	/** Whether the whole report is currently narrowed to THIS run — the state
	 *  of the card's own scope control, and the reason that control belongs on
	 *  the card: the panel has no name for a campaign, so the card the operator
	 *  is reading is the only thing that identifies one. */
	readonly picked: boolean;
	/** Whether this run's name has to lengthen to the minute to stay distinct.
	 *  A fact about the ROSTER and never about the row: a card cannot see its
	 *  siblings, so whether its own day is shared is a question only the set
	 *  can answer. False is the shortest name that is still distinct, which is
	 *  the only one worth printing. */
	readonly showsLaunchTime: boolean;
};

export type ClientSpecRows = {
	readonly os: readonly { key: string; count: number }[];
	readonly browser: readonly { key: string; count: number }[];
	readonly device: readonly { key: string; count: number }[];
	readonly viewport: readonly { key: string; count: number }[];
	readonly orientation: readonly { key: string; count: number }[];
};

export type ReportViewModel = {
	/** The controls currently in force, projected with the answer so the view
	 *  reads one reactive snapshot instead of combining state cells in render. */
	readonly range: ReportRange;
	readonly utmSources: readonly string[];
	readonly utmCampaigns: readonly string[];
	/** No answer has ever arrived, so there is nothing to hold and the tiles
	 *  shimmer in the settled layout's own geometry. */
	readonly firstLoad: boolean;
	/** Whether the report has an answer whose empty client-spec state can be
	 *  explained instead of being mistaken for a first load. */
	readonly hasAnswer: boolean;
	/** An answer is on screen and a newer one is on its way — the body holds
	 *  what it has and dims rather than blanking numbers being read. */
	readonly recomputing: boolean;
	readonly failed: boolean;
	readonly kpis: readonly KpiTile[];
	/** One point is not a trend, so a single-day range carries no plot. */
	readonly showTrend: boolean;
	readonly visitsTrend: readonly TrendPoint[];
	readonly revenueTrend: readonly TrendPoint[];
	readonly funnel: readonly FunnelRow[];
	/** What the funnel, its drop-off breakdown and its shares above were
	 *  counted over, and how well those days were read. */
	readonly funnelCoverage: FunnelCoverage;
	readonly outcomes: readonly OutcomeEntry[];
	readonly campaigns: readonly CampaignRow[];
	/** The longest bar's own visits — every channel bar is a share of this, so
	 *  the comparison across channels is a length and not a number to read. */
	readonly campaignMax: number;
	/** The paid campaigns, cost beside sales. A different subject from the
	 *  channel bars above, which count every visit by the source that sent it
	 *  and know nothing about money spent: these are the runs this shop paid
	 *  Meta for. Empty where none has run. */
	readonly campaignCosts: readonly CampaignCostRow[];
	/** Only the channels a ticket was actually collected through: a zero
	 *  segment is a channel nobody used, and naming it costs a read to learn
	 *  nothing. */
	readonly retrieval: readonly RetrievalRow[];
	readonly clientSpecs: ClientSpecRows;
	/** The current client filter, projected from the one knob object so the view
	 *  never reads a second state owner. */
	readonly clientFilters: AnalyticsReport.ClientFilter;
	readonly activeClientFilters: readonly ActiveClientFilter[];
	/** True when the current answer has no positive client-spec observation to
	 *  draw. This remains a view-model fact so the render never re-derives
	 *  emptiness from the five infographic arrays. */
	readonly clientSpecsEmpty: boolean;
	/** What the answer could read, per metric group — the contract's own four
	 *  keys, each the worst its days reported, carried whole rather than folded
	 *  to one word. Folding makes every panel inherit every other panel's gap,
	 *  which is how the devices panel came to carry the funnel's name; each
	 *  panel reads the key its own figures rest on and states no figure that
	 *  key does not support. */
	readonly coverage: AnalyticsReport.ReportCoverage;
	/** Every campaign source the answer names, whether or not one is picked —
	 *  the filter's own option list. */
	readonly campaignSources: readonly string[];
	/** A paid campaign is picked, so every figure above is that run's alone.
	 *  Read off the KNOB and not the roster, so a scope naming a campaign this
	 *  event's roster does not hold still says so and can still be cleared. */
	readonly campaignPicked: boolean;
	readonly servedFromCache: boolean;
	/** The instant, not a printed clock: whether a time reads 24-hour or AM/PM
	 *  is a fact about the reader's language, so the view prints it in its own
	 *  locale rather than the one every FIGURE is pinned to. Null until an
	 *  answer has arrived. */
	readonly computedAt: string | null;
};

const FUNNEL_STAGES = [
	{ key: "eventPageLands", labelKey: "admin.analytics.stageEventPage" },
	{ key: "contactCompleted", labelKey: "admin.analytics.stageContact" },
	{ key: "authorising", labelKey: "admin.analytics.stageAuthorising" },
	{ key: "paid", labelKey: "admin.analytics.stagePaid" },
] as const satisfies ReadonlyArray<{
	readonly key: keyof AnalyticsReport.ReportedDay["funnel"];
	readonly labelKey: MessageKey;
}>;

const RETRIEVAL_CHANNELS = [
	{ key: "download", labelKey: "admin.analytics.retrievalDownload" },
	{ key: "wallet_apple", labelKey: "admin.analytics.retrievalWalletApple" },
	{ key: "wallet_google", labelKey: "admin.analytics.retrievalWalletGoogle" },
	{ key: "ics", labelKey: "admin.analytics.retrievalIcs" },
	{ key: "handover", labelKey: "admin.analytics.retrievalHandover" },
] as const satisfies ReadonlyArray<{
	readonly key: Buyer.OrderAction;
	readonly labelKey: MessageKey;
}>;

/**
 * What each traffic verdict says on the panel.
 *
 * Three of the four put a zero in every observed figure beside them and only
 * one of those three is a fault, so each carries its own sentence: without one,
 * "Meta has not answered yet", "the ad has sent nobody yet" and "everyone it
 * sent arrived untraceable" are the same empty row, and an operator cannot tell
 * a quiet campaign from a broken join.
 */
const TRAFFIC = {
	unreported: {
		trafficLabelKey: "admin.analytics.trafficUnreported",
		trafficWhyKey: "admin.analytics.trafficUnreportedWhy",
		trafficTone: "neutral",
	},
	none: {
		trafficLabelKey: "admin.analytics.trafficNone",
		trafficWhyKey: "admin.analytics.trafficNoneWhy",
		trafficTone: "neutral",
	},
	arriving: {
		trafficLabelKey: "admin.analytics.trafficArriving",
		trafficWhyKey: "admin.analytics.trafficArrivingWhy",
		trafficTone: "success",
	},
	untraceable: {
		trafficLabelKey: "admin.analytics.trafficUntraceable",
		trafficWhyKey: "admin.analytics.trafficUntraceableWhy",
		trafficTone: "error",
	},
} as const satisfies Readonly<
	Record<
		AnalyticsReport.CampaignCost["traffic"],
		Pick<CampaignCostRow, "trafficLabelKey" | "trafficWhyKey" | "trafficTone">
	>
>;

const OUTCOMES = [
	{
		key: "soldOut",
		labelKey: "admin.analytics.outcomeSoldOut",
		tone: "error",
	},
	{
		key: "notEligible",
		labelKey: "admin.analytics.outcomeNotEligible",
		tone: "error",
	},
	{
		key: "internal",
		labelKey: "admin.analytics.outcomeInternal",
		tone: "error",
	},
	{
		key: "licenceReadOnly",
		labelKey: "admin.analytics.outcomeLicenceReadOnly",
		tone: "error",
	},
	{
		key: "abandoned",
		labelKey: "admin.analytics.outcomeAbandoned",
		tone: "neutral",
	},
	{
		key: "expired",
		labelKey: "admin.analytics.outcomeExpired",
		tone: "neutral",
	},
] as const satisfies ReadonlyArray<{
	readonly key: string;
	readonly labelKey: MessageKey;
	readonly tone: OutcomeEntry["tone"];
}>;

/** The change across a range from the data the range itself carries: its later
 *  half against its earlier half. The report answers one window at a time and
 *  carries no prior one, so this is the only comparison the panel's own data
 *  admits — and where it does not admit one (fewer than two days, an earlier
 *  half of zero), the tile carries no delta at all. */
function halfDelta(series: readonly number[]): number | null {
	if (series.length < 2) return null;
	const split = Math.floor(series.length / 2);
	const earlier = series
		.slice(0, split)
		.reduce((total, value) => total + value, 0);
	const later = series.slice(split).reduce((total, value) => total + value, 0);
	if (earlier === 0) return null;
	return Math.round(((later - earlier) / earlier) * 100);
}

function coverageFor(
	days: readonly AnalyticsReport.ReportedDay[],
	key: keyof AnalyticsReport.ReportCoverage,
): AnalyticsReport.Coverage {
	if (days.length === 0) return "unavailable";
	if (
		days.some(
			(day) =>
				day.coverage?.[key] == null || day.coverage[key] === "unavailable",
		)
	)
		return "unavailable";
	if (days.some((day) => day.coverage[key] === "partial")) return "partial";
	return "complete";
}

/**
 * Everything the panel renders, derived once. The view reads this whole and
 * derives nothing of its own, so the numbers a screenshot shows and the
 * numbers a test asserts come from one place.
 */
export const reportViewModel = computed<ReportViewModel>(() => {
	const { answer, computing, failed } = reportState.value;
	const days = answer?.days ?? [];
	const revenueCentsByDay = answer?.revenueCentsByDay ?? {};
	const {
		range,
		utmSources,
		utmCampaigns,
		client: clientFilters = {},
	} = reportKnobs.value;
	const activeClientFilters: readonly ActiveClientFilter[] =
		CLIENT_DIMENSIONS.flatMap((dimension) => {
			const value = clientFilters[dimension];
			return value == null ? [] : [{ dimension, value }];
		});

	const sum = (pick: (day: AnalyticsReport.ReportedDay) => number): number =>
		days.reduce((total, day) => total + pick(day), 0);

	const visits = sum((d) => d.visits);
	const visitors = sum((d) => d.visitors);
	const revenueCents = days.reduce(
		(total, d) => total + (revenueCentsByDay[d.date] ?? 0),
		0,
	);
	// The days the bands are counted over: every one whose `checkout_stage` arm
	// could be read. A day the arm is gone for — its checkouts collected by a
	// TTL, leaving `basket_change` events naming carts no band can be derived
	// from — is dropped here rather than blanking the range, because no later
	// pass can recover it and the range's other days are still an answer.
	const funnelDays = days.filter(
		(day) =>
			day.coverage?.funnel != null && day.coverage.funnel !== "unavailable",
	);
	const sumCarts = (
		pick: (day: AnalyticsReport.ReportedDay) => number,
	): number => funnelDays.reduce((total, day) => total + pick(day), 0);
	const stages = FUNNEL_STAGES.map((stage) => ({
		...stage,
		value: sumCarts((d) => d.funnel[stage.key]),
	}));
	const reached = stages[0]?.value ?? 0;
	const paid = stages[stages.length - 1]?.value ?? 0;
	const newBuyers = sum((d) => d.newBuyers);
	const returningBuyers = sum((d) => d.returningBuyers);
	const knownBuyers = newBuyers + returningBuyers;
	// The wire carries one median per day, never the visit spans they were
	// taken over, so a range wider than a day reports the TYPICAL DAY's median
	// — the middle day of the range's own medians, not their mean, because a
	// mean of medians is a statistic of nothing while the middle one is a real
	// day that actually happened.
	const dailyMedians = days
		.map((d) => d.medianDwellSeconds)
		.toSorted((a, b) => a - b);
	const medianDwellSeconds =
		dailyMedians[Math.floor(dailyMedians.length / 2)] ?? 0;
	// The per-day courses the tiles ride and the trend plots draw — one
	// derivation, so a sparkline and the plot under it can never disagree.
	const revenueByDay = days.map((d) => (revenueCentsByDay[d.date] ?? 0) / 100);
	const visitsByDay = days.map((d) => d.visits);
	const visitorsByDay = days.map((d) => d.visitors);
	const dwellByDay = days.map((d) => d.medianDwellSeconds);
	// Paid against VISITS — the same denominator the channel bars below use, so
	// one word means one thing on the whole surface. The other rate, of the
	// baskets started how many paid, is the funnel's own last band and is
	// printed nowhere else.
	const conversionPercent = visits === 0 ? null : (paid / visits) * 100;
	const returningPercent =
		knownBuyers === 0 ? null : (returningBuyers / knownBuyers) * 100;

	// No answer has ever arrived means there is nothing to qualify yet: the
	// panel is still in its first-load shimmer, and `coverageFor` over zero days
	// would otherwise report that emptiness as missing evidence.
	const coverage: AnalyticsReport.ReportCoverage =
		answer === null
			? {
					clientSpecs: "complete",
					visits: "complete",
					funnel: "complete",
					revenue: "complete",
				}
			: {
					clientSpecs: coverageFor(days, "clientSpecs"),
					visits: coverageFor(days, "visits"),
					funnel: coverageFor(days, "funnel"),
					revenue: coverageFor(days, "revenue"),
				};

	// The bands, their loss breakdown, and the funnel's own shares are cart
	// figures over cart figures — every one of them off `checkout_stage`
	// milestones, so one exclusion rule reaches all three and the shares stay
	// exact. That rule is per DAY and not per period: `unavailable` withholds a
	// day whole, since the stage arm recorded nothing there and one stray
	// milestone that leaked through vouches for nothing, while the days beside
	// it that DID record are counted and the surface says how many it counted. `reading` is the worst word among those counted days alone —
	// a day the bands never counted cannot make the bands they did count
	// approximate. Under `partial` they print as read, and the view marks each
	// count approximate rather than a floor: an omission does not only
	// subtract, since dropping one mid-run event splits one visit into two and
	// dropping an arrival can move a run into a campaign scope it was outside.
	//
	// The first-load case is the same one the period verdict above carries: no
	// answer has ever arrived, so a span over zero days would report that
	// emptiness as a gap somebody has to read about.
	const funnelCoverage: FunnelCoverage =
		answer === null
			? { span: "whole", countedDays: 0, rangeDays: 0, reading: "complete" }
			: {
					span:
						funnelDays.length === 0
							? "none"
							: funnelDays.length === days.length
								? "whole"
								: "some",
					countedDays: funnelDays.length,
					rangeDays: days.length,
					reading:
						funnelDays.length === 0
							? "unavailable"
							: funnelDays.some((day) => day.coverage?.funnel === "partial")
								? "partial"
								: "complete",
				};
	const funnelStatable = funnelCoverage.span !== "none";
	// The purchase rate and the paid fill on each channel bar are the two
	// figures that divide the cart arm by the VISIT arm, and the visit arm is
	// built with `checkout_stage` skipped entirely — two evidence arms, two
	// exclusion rules, so a numerator short by one unreadable milestone falls
	// against a denominator that never saw it. Nothing short of complete can
	// state them: the quotient would be wrong in a direction nothing on this
	// panel can name. This is why they read the PERIOD verdict and not the
	// span above — `complete` is the one word under which the cart arm counted
	// every day the visit arm counted, and a numerator over three days against
	// a denominator over seven is the same wrongness by a wider margin.
	const paidShareStatable = coverage.funnel === "complete";
	const purchasePercent = paidShareStatable ? conversionPercent : null;

	const campaignTotals = new Map<string, CampaignRow>();
	for (const day of days) {
		for (const [source, row] of Object.entries(day.utm)) {
			const carry = campaignTotals.get(source) ?? {
				source,
				visits: 0,
				paid: 0,
			};
			campaignTotals.set(source, {
				source,
				visits: carry.visits + row.visits,
				paid: (carry.paid ?? 0) + row.paid,
			});
		}
	}
	// Every visit that named no campaign, so the channel infographic adds
	// up to the Visits tile above it instead of silently omitting the largest
	// channel most storefronts have. Clamped: the campaign rows and the visit
	// count are two derivations of one range, and a disagreement between them
	// must not print a negative channel.
	const directVisits = Math.max(
		0,
		visits -
			[...campaignTotals.values()].reduce(
				(total, row) => total + row.visits,
				0,
			),
	);

	// Which runs share a period, so only those lengthen their name to the
	// minute. Keyed on the pair of LOCAL calendar days deliberately: a launch
	// near midnight UTC collides on the day it actually renders, which is the
	// day the operator is reading. Hoisted off the roster because a card cannot
	// see its siblings — whether a day is shared is a question only the set can
	// answer, and asking it per row would ask it once per row about all of them.
	const periodOf = (row: AnalyticsReport.CampaignCost) =>
		row.launchedAt == null
			? null
			: `${new Date(row.launchedAt).toDateString()}|${row.stoppedAt == null ? "" : new Date(row.stoppedAt).toDateString()}`;
	const shared = new Set(
		(answer?.campaigns ?? [])
			.map(periodOf)
			.filter((key, i, keys) => key !== null && keys.indexOf(key) !== i),
	);

	// Biggest spend first, which is the order the question is asked in. Not
	// narrowed by the channel chips beside it: those pick a `utm_source`, which
	// paid campaigns share with one another, so a chip could only ever hide
	// runs the operator is paying for. Not narrowed by the campaign scope these
	// same rows carry the control for either — the card an operator picked on
	// has to stay on screen, or turning the knob would be a door that locks
	// behind them. Not narrowed by an empty figure either — a campaign whose
	// every figure is zero is exactly the row whose verdict has something to
	// say.
	const campaignCosts: readonly CampaignCostRow[] = (answer?.campaigns ?? [])
		.map((row) => ({
			...row,
			...TRAFFIC[row.traffic],
			picked: utmCampaigns.includes(row.metaCampaignId),
			showsLaunchTime: shared.has(periodOf(row) ?? ""),
			costPerTicketMinor:
				row.observed == null || row.observed.ticketsSold === 0
					? null
					: row.spendMinor / row.observed.ticketsSold,
		}))
		.toSorted((a, b) => b.spendMinor - a.spendMinor);

	// Only the channels a ticket was actually collected through — a zero
	// segment is a channel nobody used, and the bar's own key would name it
	// for no reading.
	const retrieval = RETRIEVAL_CHANNELS.map((channel) => ({
		...channel,
		value: sum((d) => d.orderActions[channel.key] ?? 0),
	})).filter((row) => row.value > 0);

	const campaigns = [
		...campaignTotals.values(),
		// Scoping to a named channel or a named campaign is a question about
		// those; the visits that named neither are not part of their answer.
		...(utmCampaigns.length === 0 && directVisits > 0
			? [{ source: DIRECT_CHANNEL, visits: directVisits, paid: null }]
			: []),
	]
		.filter(
			(row) =>
				row.visits > 0 &&
				(utmSources.length === 0 || utmSources.includes(row.source)),
		)
		// Length is visits, which carry their own coverage and stand. Only the
		// bright fill and its rate are the cross-arm ratio above, so anything
		// short of complete takes the null the direct row already uses for "no
		// rate to state".
		.map((row) => ({
			...row,
			paid: paidShareStatable ? row.paid : null,
		}))
		.toSorted((a, b) => b.visits - a.visits);
	const clientTotals: Record<ClientDimension, Record<string, number>> = {
		os: {},
		browser: {},
		device: {},
		viewport: {},
		orientation: {},
	};
	for (const day of answer?.days ?? []) {
		for (const axis of CLIENT_DIMENSIONS) {
			for (const [key, count] of Object.entries(
				day.clientSpecs?.[axis] ?? {},
			)) {
				if (typeof count !== "number" || count <= 0) continue;
				clientTotals[axis][key] = (clientTotals[axis][key] ?? 0) + count;
			}
		}
	}
	const clientSpecs: ClientSpecRows = {
		os: Object.entries(clientTotals.os)
			.map(([key, count]) => ({ key, count }))
			.toSorted((a, b) => b.count - a.count),
		browser: Object.entries(clientTotals.browser)
			.map(([key, count]) => ({ key, count }))
			.toSorted((a, b) => b.count - a.count),
		device: Object.entries(clientTotals.device)
			.map(([key, count]) => ({ key, count }))
			.toSorted((a, b) => b.count - a.count),
		viewport: Object.entries(clientTotals.viewport)
			.map(([key, count]) => ({ key, count }))
			.toSorted((a, b) => b.count - a.count),
		orientation: Object.entries(clientTotals.orientation)
			.map(([key, count]) => ({ key, count }))
			.toSorted((a, b) => b.count - a.count),
	};
	const clientSpecsEmpty = CLIENT_DIMENSIONS.every(
		(dimension) => clientSpecs[dimension].length === 0,
	);

	return {
		range,
		utmSources: [...utmSources],
		utmCampaigns: [...utmCampaigns],
		firstLoad: computing && answer === null,
		hasAnswer: answer !== null,
		recomputing: computing && answer !== null,
		failed,
		// ONE row, every figure said once — where this panel's KPIs and the
		// dashboard's own sales stats reconcile: revenue appears exactly once
		// and no second row claims it. Two visual kinds and no third: a
		// sparkline where the figure has a history, a meter where it is a rate
		// against its own ceiling.
		kpis: [
			{
				key: "revenue",
				labelKey: KPI_LABEL.revenue,
				value: revenueCents,
				unit: "money",
				display: "",
				deltaPercent: halfDelta(revenueByDay),
				visual: { kind: "series", points: revenueByDay },
				noteKey: null,
			},
			{
				key: "visits",
				labelKey: KPI_LABEL.visits,
				value: visits,
				unit: "count",
				display: "",
				deltaPercent: halfDelta(visitsByDay),
				visual: { kind: "series", points: visitsByDay },
				noteKey: null,
			},
			{
				key: "visitors",
				labelKey: KPI_LABEL.visitors,
				value: visitors,
				unit: "count",
				display: "",
				deltaPercent: halfDelta(visitorsByDay),
				visual: { kind: "series", points: visitorsByDay },
				noteKey: null,
			},
			{
				key: "conversion",
				labelKey: KPI_LABEL.conversion,
				value: purchasePercent ?? 0,
				unit: "percent",
				display: purchasePercent === null ? "—" : "",
				deltaPercent: null,
				// A meter is a fill against a ceiling, so an empty one is a
				// measurement — it says "none of it", which is the same made-up
				// zero the em dash beside it exists to deny. A rate that cannot
				// be stated carries no visual at all.
				visual:
					purchasePercent === null
						? null
						: { kind: "meter", percent: purchasePercent },
				// The one tile whose dash has a cause worth naming. Gated on the
				// coverage word, never on the missing rate: the rate is also
				// missing where nobody came, and there the dash means exactly
				// that. So a complete answer never carries this line, and
				// anything short of one carries it whatever else is also true
				// of the day.
				noteKey: paidShareStatable
					? null
					: "admin.analytics.conversionCoverageIncomplete",
			},
			{
				key: "returning",
				labelKey: KPI_LABEL.returning,
				value: returningPercent ?? 0,
				unit: "percent",
				display: returningPercent === null ? "—" : "",
				deltaPercent: null,
				visual:
					returningPercent === null
						? null
						: { kind: "meter", percent: returningPercent },
				noteKey: null,
			},
			{
				key: "dwell",
				labelKey: KPI_LABEL.dwell,
				value: medianDwellSeconds,
				unit: "seconds",
				display: "",
				deltaPercent: null,
				visual: { kind: "series", points: dwellByDay },
				noteKey: null,
			},
		],
		showTrend: days.length > 1,
		visitsTrend: days.map((d) => ({ value: d.visits, date: d.date })),
		revenueTrend: days.map((d) => ({
			value: (revenueCentsByDay[d.date] ?? 0) / 100,
			date: d.date,
		})),
		funnel: stages.map((stage, i) => ({
			key: stage.key,
			labelKey: stage.labelKey,
			value: stage.value,
			share: reached === 0 ? 0 : stage.value / reached,
			droppedAfter:
				i === stages.length - 1
					? null
					: stage.value - (stages[i + 1]?.value ?? 0),
			display: funnelStatable ? "" : "—",
		})),
		funnelCoverage,
		// The same carts the bands count, over the same days: one real refusal
		// printed beside four em dashes, or counted over a day the bands above
		// excluded, would be two answers about one cohort on one panel.
		outcomes: !funnelStatable
			? []
			: OUTCOMES.map((outcome) => ({
					...outcome,
					count: sumCarts((d) =>
						outcome.key === "abandoned"
							? d.funnel.abandoned
							: outcome.key === "expired"
								? d.funnel.expired
								: (d.funnel.refused[outcome.key] ?? 0),
					),
					// A zero outcome is an outcome that did not occur, and naming
					// it costs the operator a read to learn nothing.
				})).filter((outcome) => outcome.count > 0),
		campaigns,
		campaignMax: Math.max(1, ...campaigns.map((row) => row.visits)),
		campaignCosts,
		retrieval,
		clientSpecs,
		clientFilters,
		activeClientFilters,
		clientSpecsEmpty,
		coverage,
		// The answer's own roster, not the keys of `campaignTotals` — those come
		// from `day.utm`, which a scoped answer has already narrowed, so deriving
		// the selector from them would leave it holding only what is already
		// picked.
		campaignSources: answer?.campaignSources ?? [],
		campaignPicked: utmCampaigns.length > 0,
		servedFromCache: answer?.servedFromCache ?? false,
		computedAt: answer?.computedAt ?? null,
	};
});
